import type { Editor } from "@tiptap/core";

export const MENTION_NODE_NAME = "mentionAtom";

export type MentionSuggestionState = {
  active: boolean;
  /** The trigger character of the active suggestion (e.g. `@`, `$` or `:`). */
  char: string;
  query: string;
  clientRect: (() => DOMRect | null) | null;
  command: ((props: Record<string, unknown>) => void) | null;
};

export const INACTIVE_SUGGESTION: MentionSuggestionState = {
  active: false,
  char: "",
  query: "",
  clientRect: null,
  command: null,
};

/*
The suggestion plugins live inside ProseMirror while the dropdown that renders the matches lives in
React. This store is the bridge: the plugins publish the active trigger/query and React subscribes to
it, registering a key handler so the plugin can hand over arrow/enter/escape presses.
*/
export class MentionSuggestionStore {
  private state: MentionSuggestionState = INACTIVE_SUGGESTION;

  private listeners = new Set<() => void>();

  private keyHandler: ((event: KeyboardEvent) => boolean) | null = null;

  getState = () => this.state;

  setState = (state: MentionSuggestionState) => {
    this.state = state;
    for (const listener of this.listeners) {
      listener();
    }
  };

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  setKeyHandler = (handler: ((event: KeyboardEvent) => boolean) | null) => {
    this.keyHandler = handler;
  };

  handleKeyDown = (event: KeyboardEvent) => this.keyHandler?.(event) ?? false;
}

declare module "@tiptap/core" {
  interface Storage {
    mentionAtom: { suggestion?: MentionSuggestionStore };
  }
}

export const getMentionSuggestionStore = (
  editor: Editor | null,
): MentionSuggestionStore | null =>
  editor?.storage?.[MENTION_NODE_NAME]?.suggestion ?? null;
